/**
 * Orders API utilities with caching and realtime support
 */

import { supabase } from './supabaseClient';

const getAuthHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token || localStorage.getItem('auth_token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

// Get cached orders from localStorage
export const getCachedOrders = (filter = 'all') => {
  if (typeof localStorage === 'undefined') return null;
  try {
    const cacheKey = `orders_${filter}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (e) {
    // Ignore cache errors
  }
  return null;
};

// Cache orders to localStorage
export const cacheOrders = (filter, orders) => {
  if (typeof localStorage === 'undefined') return;
  try {
    const cacheKey = `orders_${filter}`;
    localStorage.setItem(cacheKey, JSON.stringify(orders));
  } catch (e) {
    // Ignore cache errors (quota exceeded, etc.)
  }
};

/**
 * Get user's orders (as buyer or seller) with caching
 */
// Track active requests to prevent duplicates
const activeOrderRequests = new Map();

export const getOrders = async (filter = 'all', useCache = true) => {
  try {
    // Return cached orders immediately if available
    if (useCache) {
      const cached = getCachedOrders(filter);
      if (cached) {
        // Fetch fresh data in background (debounced)
        const requestKey = `orders_${filter}`;
        if (!activeOrderRequests.has(requestKey)) {
          activeOrderRequests.set(requestKey, true);
        getOrders(filter, false).then((result) => {
          if (result.data) {
            cacheOrders(filter, result.data);
          }
            activeOrderRequests.delete(requestKey);
        }).catch(() => {
            activeOrderRequests.delete(requestKey);
        });
        }
        return { data: cached, error: null };
      }
    }

    const headers = await getAuthHeaders();
    const params = filter !== 'all' ? `?filter=${filter}` : '';
    
    // Add timeout and abort controller
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    const response = await fetch(`/api/orders${params}`, {
      method: 'GET',
      headers,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = 'Failed to get orders';
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.error || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();

    // Cache the orders
    if (result.data) {
      cacheOrders(filter, result.data);
    }

    return result;
  } catch (error) {
    if (error.name === 'AbortError') {
      // Return cached data if request was aborted
      const cached = getCachedOrders(filter);
      if (cached) {
        return { data: cached, error: null };
      }
      throw new Error('Request timeout');
    }
    console.error('Error getting orders:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time order updates
 * @param {string} userId - User ID (buyer or seller)
 * @param {function} callback - Callback function that receives updated order
 * @returns {function} Unsubscribe function
 */
export const subscribeToOrders = (userId, callback) => {
  const channel = supabase
    .channel(`orders:${userId}`)
    .on(
      'postgres_changes',
      {
        event: '*', // INSERT, UPDATE, DELETE
        schema: 'public',
        table: 'orders',
        filter: `buyer_id=eq.${userId}`,
      },
      (payload) => {
        console.log('Order change detected (buyer):', payload);
        callback(payload);
      }
    )
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'orders',
        filter: `seller_id=eq.${userId}`,
      },
      (payload) => {
        console.log('Order change detected (seller):', payload);
        callback(payload);
      }
    )
    .subscribe((status) => {
      console.log('Orders subscription status:', status);
    });

  return () => {
    supabase.removeChannel(channel);
  };
};

/**
 * Accept an order (release escrow to seller)
 */
export const acceptOrder = async (orderId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/orders/${orderId}/accept`, {
      method: 'POST',
      headers,
    });

    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || 'Failed to accept order');
    }

    return result;
  } catch (error) {
    console.error('Error accepting order:', error);
    throw error;
  }
};

/**
 * Mark order as delivered (seller action)
 */
export const markOrderAsDelivered = async (orderId) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/orders/${orderId}/mark-delivered`, {
      method: 'POST',
      headers,
    });

    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || 'Failed to mark order as delivered');
    }

    return result;
  } catch (error) {
    console.error('Error marking order as delivered:', error);
    throw error;
  }
};

